import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Users,
  TrendingUp,
  ArrowRightLeft,
  Calendar,
  Target,
  UserCheck,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

/* ---------- inline CSS just for this file ---------- */
const CLAMP_CSS = `
  .clamp-wrap { position: relative; --surface: #fff; }
  .profile-id-unique { margin: 2px 0 0; color: var(--text-secondary); font-size: 12px; font-weight: 600; }
  .clamp-para {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--clamp-lines, 3);
    overflow: hidden;
    margin-bottom: 0;
  }
  .clamp-para.expanded {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
  }

  /* removed the fade pseudo-element */

  .clamp-toggle {
    border: 0; background: none; cursor: pointer;
    font: inherit; color: inherit;
  }
  .clamp-toggle.clamp-ellipsis {
    position: absolute; right: 0; bottom: .1rem;
    padding-left: 8px;
    /* remove gradient background to avoid any fade effect */
    background: none;
    font-size: 1.25rem; line-height: 1;
  }
  .clamp-toggle.clamp-less {
    margin-top: 6px; text-decoration: underline; color: #555;
  }
`;

function InlineClampStyles() {
  return <style>{CLAMP_CSS}</style>;
}
/* --------------------------------------------------- */

export function ClampableText({
  text = "",
  lines = 3,
  className = "",
  mobileEllipsisText = "…",
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const pRef = useRef(null);

  // Detect if clamping actually truncates (only when collapsed)
  useEffect(() => {
    if (!pRef.current || expanded) return;
    const el = pRef.current;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    setIsTruncated(overflowing);
  }, [text, expanded, lines]);

  return (
    <div
      className={`clamp-wrap ${expanded ? "expanded" : ""}`}
      style={{ "--clamp-lines": lines }}
    >
      <p
        style={{ marginBottom: 0 }}
        ref={pRef}
        className={`team-description clamp-para ${
          expanded ? "expanded" : ""
        } ${className}`}
      >
        {text}
      </p>

      {!expanded && isTruncated && (
        <button
          type="button"
          className="clamp-toggle clamp-ellipsis"
          aria-label="Show full description"
          data-mobile-label={mobileEllipsisText}
          onClick={() => setExpanded(true)}
        >
          …
        </button>
      )}

      {expanded && (
        <button
          type="button"
          className="clamp-toggle clamp-less"
          onClick={() => setExpanded(false)}
        >
          {t("common.showLess")}
        </button>
      )}
    </div>
  );
}

function ProfileCard(props) {
  const { t } = useTranslation();
  useEffect(() => {
    console.log("ProfileCard:", props);
  }, [props]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isTeamContext = props.context === "team" || props.isTeamPage === true;

  const teamPerformance =
    props.performance ?? props.safeCandidate?.teamPerformance;

  const teamOpenPositions =
    props.openPositions ?? props.safeCandidate?.openPositions;

  const getPerformanceColor = (performance) => {
    if (performance >= 90) return "performance-excellent";
    if (performance >= 80) return "performance-good";
    return "performance-average";
  };

  const onClickSkillHandler = (skill) => {
    // Stay on Team Detail page and switch to "Add Members" tab
    if (typeof props.onSkillsToDevelopClick === "function") {
      props.onSkillsToDevelopClick();

      // After tab switch, scroll the Add Members content into view
      setTimeout(() => {
        const el = document.getElementById("add-members-tab");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300); // small delay to ensure the tab content is mounted

      return;
    }

    // Fallback (if component is used elsewhere)
    setSearchParams({ userId: skill._id });
    navigate(`/talent-development/upskill?userId=${skill._id}`);
  };

  const onClickLeadershipHandler = (skill) => {
    const allUserIds = Array.isArray(props?.safeCandidate?.members)
      ? props.safeCandidate.members.map((m) => m?._id).filter(Boolean)
      : [];

    // Fallback to the clicked item if members not available
    const idsToSend = allUserIds.length
      ? allUserIds
      : [skill._id].filter(Boolean);

    // Persist for downstream consumers (unchanged behavior)
    try {
      sessionStorage.setItem("td_userIds", JSON.stringify(idsToSend));
    } catch {}

    // Build URL with repeated userId params: ?userId=a&userId=b&userId=c
    const qs = new URLSearchParams();
    idsToSend.forEach((id) => qs.append("userId", id));

    navigate(`/talent-development/leadership?${qs.toString()}`, {
      state: { userIds: idsToSend },
    });
  };

  const onClickMobilityHandler = (skill) => {
    const allUserIds = Array.isArray(props?.safeCandidate?.members)
      ? props.safeCandidate.members.map((m) => m?._id).filter(Boolean)
      : [];

    const idsToSend = allUserIds.length
      ? allUserIds
      : [skill?._id || props?.safeCandidate?._id].filter(Boolean);

    try {
      sessionStorage.setItem("td_userIds", JSON.stringify(idsToSend));
    } catch {}

    const qs = new URLSearchParams();
    idsToSend.forEach((id) => qs.append("userId", id));

    // coming from employee/candidate profile
    qs.append("source", "employee");

    navigate(`/talent-development/mobility?${qs.toString()}`, {
      state: { userIds: idsToSend, source: "employee" },
    });
  };

  return (
    <>
      {/* inject CSS for clamp right here */}
      <InlineClampStyles />

      <div className="profile-header-unique">
        <div className="profile-avatar-unique">{props.icon}</div>

        <div className="profile-info-unique">
          <h1>{props.safeCandidate?.name || props.safeCandidate?.teamName}</h1>
          <p className="profile-role-unique">{props.safeCandidate?.role}</p>
          {!isTeamContext && (
            <p className="profile-id-unique">
              Profile ID:{" "}
              {props.safeCandidate?.profileId ||
                props.safeCandidate?.employeeId ||
                props.safeCandidate?.userId ||
                props.safeCandidate?._id ||
                props.safeCandidate?.id ||
                "-"}
            </p>
          )}
          <p className="profile-department-unique">
            {props.safeCandidate?.department}
          </p>

          {/* ⬇️ clamped to 3 lines with inline CSS from above */}
          <ClampableText
            text={props.safeCandidate?.description || ""}
            lines={3}
            mobileEllipsisText={isTeamContext ? "(...)" : "…"}
          />

          <div className="profile-meta-unique">
            <span
              className={`meta-item-unique ${isTeamContext ? "team-created-row" : ""}`}
            >
              <Calendar size={16} />
              <span>
                {props.safeCandidate?.joinDate
                  ? t("profileCard.joined")
                  : t("profileCard.created")}{" "}
              </span>
              {props.safeCandidate?.joinDate
                ? new Date(props.safeCandidate?.joinDate).toLocaleDateString()
                : props.safeCandidate?.createdAt
                  ? new Date(
                      props.safeCandidate?.createdAt,
                    ).toLocaleDateString()
                  : t("common.notAvailableShort")}
            </span>

            {teamOpenPositions != null && (
              <span className="meta-item-unique team-open-positions-meta">
                <Target size={14} />
                {teamOpenPositions} open positions
              </span>
            )}

            {isTeamContext && teamPerformance != null && (
              <span
                className={`meta-item-unique team-performance-meta ${getPerformanceColor(
                  teamPerformance,
                )}`}
                style={{ borderRadius: "20px", padding: "4px 8px" }}
              >
                <TrendingUp size={14} />
                {teamPerformance}%
              </span>
            )}
          </div>
        </div>

        <div className="profile-stats-wrap-unique">
          {!isTeamContext && teamPerformance != null && (
            <div className="team-performance">
              <div
                className={`performance-score ${getPerformanceColor(
                  teamPerformance,
                )}`}
              >
                <TrendingUp size={20} />
                <span>{teamPerformance}%</span>
              </div>
            </div>
          )}

          <div className="stats-links">
            <button
              className="stat-link-header upskill"
              onClick={() => onClickSkillHandler(props.safeCandidate)}
              title={t("profileCard.viewUpskillingOpportunities")}
            >
              <TrendingUp size={16} />
              <div className="stat-content">
                <span className="stat-number">
                  {props.candidateStats?.upskill?.count ??
                    props.safeCandidate?.skillsToDevelop ??
                    30}
                </span>
                <span className="stat-label">
                  {t("profileCard.skillsToDevelop")}
                </span>
              </div>
            </button>

            <button
              className="stat-link-header leadership"
              onClick={() => onClickLeadershipHandler(props.safeCandidate)}
              title={t("profileCard.viewLeadershipReadiness")}
            >
              <UserCheck size={16} />
              <div className="stat-content">
                <span className="stat-number">
                  {isTeamContext
                    ? (props?.safeCandidate?.leadershipReadyScore ??
                      props?.safeCandidate?.leadershipReady ??
                      0) // no %
                    : `${
                        props?.safeCandidate?.leadershipReadyScore ??
                        props?.safeCandidate?.leadershipReady ??
                        0
                      }%`}
                </span>
                <span className="stat-label">
                  {isTeamContext
                    ? t("profileCard.leadershipReady")
                    : t("profileCard.leadershipPotential")}
                </span>
              </div>
            </button>

            <button
              className="stat-link-header mobility"
              onClick={() => onClickMobilityHandler(props.safeCandidate)}
              title={t("profileCard.viewMobilityOpportunities")}
            >
              <ArrowRightLeft size={16} />
              <div className="stat-content">
                <span className="stat-number">
                  {props?.safeCandidate?.mobilityOpportunities ?? 0}
                </span>
                <span className="stat-label">
                  {t("profileCard.mobilityOpportunities")}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProfileCard;
